import { useMemo, useState } from 'react'
import { useFiscalYear } from '@/lib/fiscalYear'
import { Link } from 'react-router-dom'
import {
  budgetHealth,
  getFiscalYearLabel,
  sumDollars,
  type BudgetTotals,
  type BudgetCode,
} from '@budgetmonkey/shared'
import { Badge, Button, Card, EmptyState, ErrorNote, Money } from '@/components/ui'
import { LoadingOrOffline } from '@/lib/loadState'
import { BalanceStrip } from '@/features/budgetCodes/BalanceStrip'
import {
  useExternalTransactions,
  useOwnedCodeTotals,
} from '@/features/transactions/hooks'
import { humanErrorMessage } from '@/lib/errorMessage'

/**
 * Dashboard — REQUIREMENTS.md §7.4, §8.2.
 *
 * Owned codes only. External codes have no balance to track, so they appear
 * as a single badge linking to §8.2b — and that badge is hidden entirely when
 * there are none, rather than sitting there as permanent clutter.
 *
 * Totals are derived client-side from the fiscal year's transactions. At
 * fewer than 200 transactions per code per year this is fast, and a derived
 * number cannot drift from the ledger the way a stored counter can.
 */
export function DashboardScreen() {
  const fiscalYear = useFiscalYear()
  const { data: codeTotals, state, error } = useOwnedCodeTotals(fiscalYear)
  const { data: externalTransactions } = useExternalTransactions(fiscalYear)
  const [hideZeroActivity, setHideZeroActivity] = useState(false)

  const visible = useMemo(
    () =>
      hideZeroActivity
        ? codeTotals.filter((c) => c.totals.transactionCount > 0)
        : codeTotals,
    [codeTotals, hideZeroActivity],
  )

  const zeroActivityCount = codeTotals.length - codeTotals.filter(
    (c) => c.totals.transactionCount > 0,
  ).length

  const grandTotals = useMemo(
    () => ({
      beginning: sumDollars(codeTotals.map((c) => c.totals.beginning)),
      spent: sumDollars(codeTotals.map((c) => c.totals.spent)),
      remaining: sumDollars(codeTotals.map((c) => c.totals.remaining)),
      confirmed: sumDollars(codeTotals.map((c) => c.totals.confirmed)),
    }),
    [codeTotals],
  )

  const externalTotal = sumDollars(
    externalTransactions
      .filter(({ transaction }) => transaction.status !== 'void')
      .map(({ transaction }) => transaction.amount),
  )

  const flagged = codeTotals.filter(
    ({ code, totals }) => budgetHealth(totals, code.alertThresholdPercent) !== 'ok',
  )

  // Before the empty state, never after it: "no budget codes" is a claim about
  // this year's data, and it may only be made once a query has answered.
  if (state !== 'ready') {
    return (
      <div className="space-y-5">
        <Header fiscalYear={fiscalYear} />
        <LoadingOrOffline state={state} what="Your budget" />
      </div>
    )
  }

  if (error != null) {
    return <ErrorNote>{humanErrorMessage(error, "Couldn't load the dashboard.")}</ErrorNote>
  }

  if (codeTotals.length === 0) {
    return (
      <div className="space-y-5">
        <Header fiscalYear={fiscalYear} />
        <EmptyState title={`No budget codes for ${getFiscalYearLabel(fiscalYear)}`}>
          <p>
            <Link to="/setup" className="underline underline-offset-4">
              Set up this fiscal year
            </Link>{' '}
            to load your eleven codes and enter beginning balances.
          </p>
        </EmptyState>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Header fiscalYear={fiscalYear} />

      <Card className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          All owned codes
        </h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
          <Total label="Beginning" value={grandTotals.beginning} />
          <Total label="Spent" value={grandTotals.spent} />
          <Total
            label="Remaining"
            value={grandTotals.remaining}
            tone={grandTotals.remaining < 0 ? 'danger' : 'normal'}
          />
          <Total label="Confirmed" value={grandTotals.confirmed} />
        </div>
      </Card>

      {flagged.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Needs attention
          </h2>
          <ul className="space-y-2">
            {flagged.map(({ code, totals }) => {
              const health = budgetHealth(totals, code.alertThresholdPercent)
              return (
                <li key={code.id}>
                  <Link
                    to={`/codes/${code.id}`}
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 transition ${
                      health === 'over'
                        ? 'border-red-200 bg-red-50 hover:bg-red-100'
                        : 'border-amber-200 bg-amber-50 hover:bg-amber-100'
                    }`}
                  >
                    <span className="font-medium">{code.shortLabel}</span>
                    <span
                      className={`text-sm ${
                        health === 'over' ? 'text-red-800' : 'text-amber-900'
                      }`}
                    >
                      {health === 'over' ? (
                        <>
                          Overspent by <Money value={Math.abs(totals.remaining)} />
                        </>
                      ) : (
                        <>
                          {Math.round(totals.spentFraction * 100)}% spent ·{' '}
                          <Money value={totals.remaining} /> left
                        </>
                      )}
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {/* §7.4 step 6: hidden entirely when there are no external charges. */}
      {externalTransactions.length > 0 && (
        <Link
          to="/external"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 transition hover:bg-slate-50"
        >
          <span className="text-sm text-slate-700">
            {externalTransactions.length} external charge
            {externalTransactions.length === 1 ? '' : 's'} —{' '}
            <Money value={externalTotal} className="font-medium" />
          </span>
          <span className="text-sm text-slate-500 underline underline-offset-4">
            View
          </span>
        </Link>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Budget codes ({visible.length})
          </h2>
          {zeroActivityCount > 0 && (
            <Button variant="ghost" onClick={() => setHideZeroActivity((v) => !v)}>
              {hideZeroActivity
                ? `Show ${zeroActivityCount} with no activity`
                : 'Hide codes with no activity'}
            </Button>
          )}
        </div>

        <ul className="grid gap-4 lg:grid-cols-2">
          {visible.map(({ code, totals }) => (
            <li key={code.id}>
              <CodeCard code={code} totals={totals} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

function Header({ fiscalYear }: { fiscalYear: number }) {
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
      <p className="text-sm text-slate-500">{getFiscalYearLabel(fiscalYear)}</p>
    </div>
  )
}

function Total({
  label,
  value,
  tone = 'normal',
}: {
  label: string
  value: number
  tone?: 'normal' | 'danger'
}) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p
        className={`text-xl font-semibold ${
          tone === 'danger' ? 'text-red-700' : 'text-slate-900'
        }`}
      >
        <Money value={value} />
      </p>
    </div>
  )
}

function CodeCard({ code, totals }: { code: BudgetCode; totals: BudgetTotals }) {
  return (
    <Card className="h-full space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to={`/codes/${code.id}`}
            className="font-medium text-slate-900 underline-offset-4 hover:underline"
          >
            {code.shortLabel}
          </Link>
          <p className="font-mono text-xs text-slate-500">{code.code}</p>
        </div>
        <div className="flex gap-1.5">
          {code.fund === 'grant' && <Badge tone="ok">Grant</Badge>}
          {totals.transactionCount > 0 && (
            <Badge>
              {totals.transactionCount} txn{totals.transactionCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      </div>

      <BalanceStrip
        totals={totals}
        alertThresholdPercent={code.alertThresholdPercent}
        size="compact"
      />
    </Card>
  )
}
